import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useStatuses } from "@/lib/useStatuses";
import { matches } from "@/lib/text";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Phone, MessageCircle, Search, Hash, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : "—");

function ChannelIcon({ ch }) {
  return ch === "whatsapp" || ch === "simulator"
    ? <MessageCircle className="w-3.5 h-3.5 text-emerald-500" />
    : <Users className="w-3.5 h-3.5" />;
}

export default function Customers() {
  const { styleOf } = useStatuses();
  const { hasPerm } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newName, setNewName] = useState("");

  const load = () => api.get("/customers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (open) api.get(`/customers/${encodeURIComponent(open.phone)}/orders`).then((r) => setOrders(r.data));
  }, [open]);

  const startEdit = (e, c) => { e.stopPropagation(); setEditing(c); setNewName(c.name); };

  const removeCustomer = async (e, c) => {
    e.stopPropagation();
    const msg = c.total_os > 0
      ? `Excluir o cliente "${c.name}"?\n\nEle possui ${c.total_os} OS. As OS e o histórico serão mantidos; apenas o cadastro do cliente será removido (no próximo contato pelo WhatsApp o nome será perguntado novamente).`
      : `Excluir o cliente "${c.name}" (${c.phone})?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/customers/${encodeURIComponent(c.phone)}`);
      toast.success("Cliente excluído");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  const saveName = async () => {
    try {
      await api.patch(`/customers/${encodeURIComponent(editing.phone)}`, { name: newName });
      toast.success("Nome do cliente atualizado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const filtered = list.filter((c) => matches(q, c.name, c.phone));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          Clientes
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Contatos cadastrados pelo WhatsApp ou pelo painel. Clique no lápis para corrigir o nome.
        </p>
      </div>

      <Card className="p-4 border-slate-200 dark:border-slate-800">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="customer-search-input" placeholder="Buscar por nome ou telefone..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-slate-200 dark:border-slate-800">
          <Users className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">Nenhum cliente ainda. Assim que alguém mandar mensagem no seu WhatsApp e informar o nome, aparecerá aqui.</p>
        </Card>
      ) : (
        <Card className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="customers-table">
          <div className="hidden md:grid grid-cols-[1fr_160px_90px_90px_120px_100px_96px] gap-3 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
            <span>Nome</span><span>Telefone</span><span className="text-center">Total OS</span><span className="text-center">Em aberto</span><span>Último contato</span><span>Canal</span><span />
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map((c) => (
              <li
                key={c.phone}
                onClick={() => setOpen(c)}
                data-testid={`customer-row-${c.phone}`}
                className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_160px_90px_90px_120px_100px_96px] gap-x-3 gap-y-1 items-center px-4 md:px-5 py-3 hover:bg-emerald-50/50 dark:hover:bg-slate-800/60 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white text-sm font-bold">
                    {c.name[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 dark:text-white truncate" data-testid={`customer-name-${c.phone}`}>{c.name}</p>
                    <p className="md:hidden text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</p>
                  </div>
                </div>
                <span className="hidden md:flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300 font-mono"><Phone className="w-3 h-3 text-slate-400" /> {c.phone}</span>
                <span className="hidden md:block text-center font-bold text-slate-900 dark:text-white">{c.total_os}</span>
                <span className="hidden md:block text-center font-bold text-amber-600 dark:text-amber-400">{c.open_os}</span>
                <span className="hidden md:block text-sm text-slate-600 dark:text-slate-300">{fmtDate(c.last_contact)}</span>
                <span className="hidden md:flex items-center gap-1 text-xs text-slate-500"><ChannelIcon ch={c.last_channel} /> {c.last_channel || "—"}</span>
                <div className="flex items-center justify-end gap-2 row-span-2 md:row-span-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => startEdit(e, c)} data-testid={`edit-customer-${c.phone}`} aria-label="Editar nome">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {hasPerm("delete_os") && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={(e) => removeCustomer(e, c)} data-testid={`delete-customer-${c.phone}`} aria-label="Excluir cliente">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <div className="md:hidden col-span-1 flex items-center gap-3 text-xs text-slate-500 pl-12">
                  <span><b className="text-slate-900 dark:text-white">{c.total_os}</b> OS</span>
                  <span><b className="text-amber-600">{c.open_os}</b> em aberto</span>
                  <span className="flex items-center gap-1"><ChannelIcon ch={c.last_channel} /> {fmtDate(c.last_contact)}</span>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white dark:bg-slate-900 max-w-md">
          <DialogHeader><DialogTitle>Editar nome do cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {editing?.phone}</p>
            <div>
              <Label>Nome</Label>
              <Input data-testid="customer-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName()} autoFocus />
              <p className="text-xs text-slate-500 mt-1">O novo nome será aplicado em todas as OS deste telefone.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="cancel-customer-name-btn">Cancelar</Button>
            <Button onClick={saveName} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-customer-name-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-white dark:bg-slate-900 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold">{open?.name?.[0]?.toUpperCase()}</div>
              <div>
                <p>{open?.name}</p>
                <p className="text-xs font-normal text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {open?.phone}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {orders.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Sem OS registradas.</p>
            ) : (
              orders.map((o) => (
                <Link key={o.id} to={`/orders/${o.id}`} onClick={() => setOpen(null)} data-testid={`customer-os-${o.number}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  <Hash className="w-4 h-4 text-slate-400" />
                  <span className="font-mono font-bold text-slate-900 dark:text-white">{o.number}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 dark:text-white truncate">{o.title}</p>
                    <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("pt-BR")}</p>
                  </div>
                  <Badge variant="outline" className="text-xs" style={styleOf(o.status)}>{o.status}</Badge>
                </Link>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
